import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Alert,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";

import API from "../utils/api";
import BoardSummaryStrip from "../components/intelligence/BoardSummaryStrip";
import BoardFilterBar from "../components/intelligence/BoardFilterBar";
import BoardLane from "../components/intelligence/BoardLane";
import { LANE_KEYS, LANE_CONFIG } from "../components/intelligence/boardConfig";

const DEFAULT_FILTERS = {
  territory_id: null,
};

const EMPTY_BOARD = {
  summary: {
    upcoming_follow_up: 0,
    overdue: 0,
    system_followed_up: 0,
    at_risk: 0,
    total: 0,
  },
  lanes: {
    upcoming_follow_up: [],
    overdue: [],
    system_followed_up: [],
    at_risk: [],
  },
};

function buildQueryString(filters) {
  const params = new URLSearchParams();
  if (filters.territory_id != null)
    params.set("territory", String(filters.territory_id));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export default function FollowUpIntelligencePage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [board, setBoard] = useState(EMPTY_BOARD);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activeMobileLane, setActiveMobileLane] = useState("overdue");
  const [territories, setTerritories] = useState([]);

  const fetchBoard = useCallback(async (activeFilters) => {
    setLoading(true);
    setError(null);
    try {
      const qs = buildQueryString(activeFilters);
      const res = await API.get(`intelligence/followup-board/${qs}`);
      setBoard(res.data ?? EMPTY_BOARD);
    } catch (err) {
      console.error("FollowUpIntelligence fetch error:", err);
      setError(
        err?.response?.status === 403
          ? "You do not have permission to view Follow-Up Intelligence."
          : "Unable to load the board. Please try again."
      );
      setBoard(EMPTY_BOARD);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch territories once on mount for the filter dropdown
  useEffect(() => {
    API.get("/territories/")
      .then((res) => setTerritories(res.data || []))
      .catch(() => setTerritories([]));
  }, []);

  useEffect(() => {
    fetchBoard(filters);
  }, [filters, fetchBoard]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        background: isDark
          ? "radial-gradient(circle at top, rgba(37,99,235,0.10), transparent 28%), linear-gradient(180deg, #0b1220 0%, #0f172a 100%)"
          : "radial-gradient(circle at top, rgba(37,99,235,0.08), transparent 24%), linear-gradient(180deg, #f8fafc 0%, #f4f7fb 100%)",
        p: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1700, mx: "auto" }}>

        {/* Page header */}
        <Box
          sx={{
            borderRadius: "20px",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
            background: isDark
              ? "linear-gradient(135deg, rgba(15,23,42,0.84) 0%, rgba(30,41,59,0.88) 100%)"
              : "linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.98) 100%)",
            backdropFilter: "blur(12px)",
            boxShadow: isDark
              ? "0 10px 28px rgba(0,0,0,0.24)"
              : "0 10px 28px rgba(15,23,42,0.06)",
            px: { xs: 2.5, md: 3.5 },
            py: { xs: 2.5, md: 2.8 },
            mb: 3,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              {/* Phase badge */}
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.4,
                  borderRadius: "20px",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  border: `1px solid ${isDark ? "rgba(14,165,233,0.3)" : "rgba(14,165,233,0.4)"}`,
                  bgcolor: isDark ? "rgba(14,165,233,0.12)" : "rgba(14,165,233,0.07)",
                  color: isDark ? "#7dd3fc" : "#0369a1",
                  mb: 1,
                }}
              >
                Territory Intelligence · Phase 1
              </Box>

              <Stack direction="row" alignItems="center" spacing={1}>
                <TrackChangesIcon sx={{ fontSize: 22, color: "text.secondary" }} />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.03em",
                    fontSize: { xs: "1.45rem", md: "1.8rem" },
                    lineHeight: 1.05,
                  }}
                >
                  Follow-Up Intelligence
                </Typography>
              </Stack>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, fontSize: "0.83rem" }}
              >
                Manager view of upcoming, overdue, and at-risk follow-ups across your team.
              </Typography>
            </Box>

          </Stack>
        </Box>

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: "14px" }}>
            {error}
          </Alert>
        )}

        {/* Summary strip */}
        <Box
          data-tour="follow-up-summary"
          sx={{
            borderRadius: "20px",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
            background: isDark ? "rgba(15,23,42,0.58)" : "rgba(255,255,255,0.84)",
            backdropFilter: "blur(12px)",
            boxShadow: isDark
              ? "0 10px 28px rgba(0,0,0,0.20)"
              : "0 10px 28px rgba(15,23,42,0.05)",
            mb: 2.25,
            p: { xs: 1.25, md: 1.5 },
          }}
        >
          <Stack spacing={1.1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ px: 0.5 }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  fontSize: "0.7rem",
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "text.primary",
                }}
              >
                Summary Snapshot
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: "text.disabled",
                  fontStyle: "italic",
                }}
              >
                Priority focus: Overdue follow-ups
              </Typography>
            </Stack>
            <BoardSummaryStrip summary={board.summary} loading={loading} />
          </Stack>
        </Box>

        {/* Filter bar */}
        <Box
          data-tour="follow-up-filters"
          sx={{
            borderRadius: "20px",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
            background: isDark ? "rgba(15,23,42,0.56)" : "rgba(255,255,255,0.86)",
            backdropFilter: "blur(12px)",
            boxShadow: isDark
              ? "0 10px 28px rgba(0,0,0,0.20)"
              : "0 10px 28px rgba(15,23,42,0.05)",
            mb: 2.25,
            p: { xs: 1.25, md: 1.5 },
          }}
        >
          <Stack spacing={1}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                fontSize: "0.7rem",
                letterSpacing: "0.07em",
                textTransform: "uppercase",
                color: "text.primary",
                px: 0.5,
              }}
            >
              Filters
            </Typography>
            <BoardFilterBar
              filters={filters}
              onChange={handleFiltersChange}
              territories={territories}
            />
          </Stack>
        </Box>

        {/* Board zone */}
        <Box
          data-tour="follow-up-board"
          sx={{
            borderRadius: "20px",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
            background: isDark ? "rgba(15,23,42,0.52)" : "rgba(255,255,255,0.80)",
            backdropFilter: "blur(12px)",
            boxShadow: isDark
              ? "0 10px 28px rgba(0,0,0,0.22)"
              : "0 10px 28px rgba(15,23,42,0.05)",
            p: { xs: 1.25, md: 1.75 },
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              display: "block",
              fontSize: "0.7rem",
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: "text.primary",
              mb: 1.5,
              px: 0.5,
            }}
          >
            Follow-Up Board
          </Typography>

          {isMobile ? (
            /* ── Mobile: tab per lane ── */
            <Box>
              <Tabs
                value={activeMobileLane}
                onChange={(_, val) => setActiveMobileLane(val)}
                variant="fullWidth"
                sx={{
                  mb: 2,
                  p: 0.5,
                  borderRadius: "16px",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
                  background: isDark ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,0.95)",
                  "& .MuiTabs-indicator": { display: "none" },
                  "& .MuiTabs-flexContainer": { gap: 0.5 },
                  "& .MuiTab-root": {
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    minHeight: 38,
                    minWidth: 0,
                    px: 0.75,
                    color: "text.secondary",
                    transition: "all 0.18s ease",
                  },
                  "& .Mui-selected": {
                    color: `${LANE_CONFIG[activeMobileLane]?.dotColor} !important`,
                    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.94)",
                    boxShadow: isDark
                      ? "0 4px 14px rgba(0,0,0,0.18)"
                      : "0 4px 14px rgba(15,23,42,0.06)",
                  },
                }}
              >
                {LANE_KEYS.map((key) => {
                  const count = board.summary?.[key] ?? 0;
                  const config = LANE_CONFIG[key];
                  return (
                    <Tab
                      key={key}
                      value={key}
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <span>{config.mobileLabel}</span>
                          {!loading && count > 0 && (
                            <Box
                              component="span"
                              sx={{
                                px: 0.7,
                                py: 0,
                                borderRadius: "8px",
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                lineHeight: 1.8,
                                bgcolor: isDark ? config.chipBgDark : config.chipBgLight,
                                color: isDark ? config.chipTextDark : config.chipTextLight,
                              }}
                            >
                              {count}
                            </Box>
                          )}
                        </Stack>
                      }
                    />
                  );
                })}
              </Tabs>

              <BoardLane
                laneKey={activeMobileLane}
                cards={board.lanes?.[activeMobileLane] ?? []}
                loading={loading}
              />
            </Box>
          ) : (
            /* ── Desktop: 4-column grid ── */
            <Box sx={{ overflowX: "auto", pb: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(300px, 1fr))",
                  gap: 2,
                  minWidth: 1280,
                }}
              >
                {LANE_KEYS.map((laneKey) => (
                  <BoardLane
                    key={laneKey}
                    laneKey={laneKey}
                    cards={board.lanes?.[laneKey] ?? []}
                    loading={loading}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

      </Box>
    </Box>
  );
}
